<template>
  <div>
    <base-heading>Add Client</base-heading>
    <hr class="divider">
    <validation-observer ref="addClientForm">
      <div v-if="externalIdOnly">
        <validation-provider
          v-if="externalIdOnly"
          v-slot="{ touched, pristine, valid, invalid, validated }"
          mode="passive"
          name="id"
          rules="required"
        >
          <input
            v-model.trim="client.external_id"
            type="text"
            class="form-control"
            :class="{
              'is-invalid':
                validated && invalid,
            }"
            name="first_name"
            :placeholder="`ID${validated && invalid ? ' (Required)' : ''}`"
            tabindex="1"
            :disabled="loading"
            @click="onInputClicked"
          >
        </validation-provider>
      </div>
      <div v-else class="add-client-grid">
        <div>
          <img
            v-if="image"
            :src="image"
            alt
            class="avatar"
            @click="loadImage"
          >
          <div v-else class="position-relative">
            <img
              :src="require('@/assets/upload-avatar-placeholder.svg').default"
              class="avatar-placeholder"
              alt
              @click="loadImage"
            >
            <img
              :src="require('@/assets/upload-avatar-placeholder-add-icon.svg').default"
              class="avatar-placeholder__add-icon"
              alt
              @click="loadImage"
            >
          </div>
          <input
            ref="upload"
            type="file"
            class="d-none"
            accept="image/png, image/jpeg"
            @change="readFile"
          >
        </div>
        <div class="add-client-grid__form">
          <div class="form-grid">
            <validation-provider
              ref="first_name_provider"
              v-slot="{ touched, pristine, valid, invalid, validated }"
              mode="passive"
              name="first name"
              :rules="{ required: isRequiredField('first_name') }"
            >
              <input
                v-model.trim="client.first_name"
                type="text"
                class="form-control"
                :class="{
                  'is-invalid':
                    validated && invalid,
                }"
                name="first_name"
                :placeholder="`${customerFieldDisplayNames['first_name']}${validated && invalid && isRequiredField('first_name') ? ' (Required)' : ''}`"
                tabindex="1"
                :disabled="loading"
                @click="onInputClicked"
              >
            </validation-provider>

            <validation-provider
              ref="last_name_provider"
              v-slot="{ touched, pristine, valid, invalid, validated }"
              mode="passive"
              name="last name"
              :rules="{ required: isRequiredField('last_name') }"
            >
              <input
                v-model.trim="client.last_name"
                type="text"
                :class="{
                  'is-invalid':
                    validated && invalid,
                }"
                name="last_name"
                class="form-control"
                :placeholder="`${customerFieldDisplayNames['last_name']}${validated && invalid && isRequiredField('last_name') ? ' (Required)' : ''}`"
                tabindex="2"
                :disabled="loading"
                @click="onInputClicked"
              >
            </validation-provider>

            <validation-provider
              ref="email_provider"
              v-slot="{ touched, pristine, valid, invalid, validated }"
              mode="passive"
              name="email"
              :rules="{
                required: isRequiredField('email'),
                email: isRequiredField('email'),
              }"
            >
              <input
                v-model.trim="client.email"
                :required="emailRequired"
                type="email"
                :class="{
                  'is-invalid':
                    validated && invalid,
                }"
                name="email"
                class="form-control"
                :placeholder="`${customerFieldDisplayNames['email']}${validated && invalid && isRequiredField('email') ? ' (Required)' : ''}`"
                tabindex="3"
                :disabled="loading"
                @input="isEmail()"
                @click="onInputClicked"
              >
            </validation-provider>

            <validation-provider
              ref="phone_provider"
              v-slot="{ touched, pristine, valid, invalid, validated }"
              mode="passive"
              name="phone_number"
            >
              <input
                v-model.trim="client.phone_number"
                :required="phone_numberRequired"
                type="text"
                name="phone_number"
                class="form-control"
                :class="{
                  'is-invalid':
                    validated && invalid,
                }"
                :placeholder="`${customerFieldDisplayNames['phone_number']}${validated && invalid && isRequiredField('phone_number') ? ' (Required)' : ''}`"
                tabindex="4"
                :disabled="loading"
                @input="isPhoneNumber()"
              >
            </validation-provider>

            <input
              v-model.trim="client.location"
              type="text"
              name="location"
              class="form-control"
              :placeholder="customerFieldDisplayNames['location']"
              tabindex="5"
              :disabled="loading"
            >

            <input
              v-model="client.birth_date"
              v-mask="mask"
              type="text"
              name="birthday"
              class="form-control"
              :placeholder="customerFieldDisplayNames['birth_date']"
              tabindex="6"
              :disabled="loading"
            >
          </div>
          <div
            v-if="isAdminMode && permissionViewsAllClient"
            class="profile-selector"
          >
            <profile-selector-container v-model="assigned_team_owner" />
          </div>
        </div>
      </div>
      <div class="buttons-container d-flex justify-content-end" :class="{ 'buttons-container__small': externalIdOnly }">
        <base-button
          class="add-client-cancel-button"
          text
          @click="$emit('closeModal')"
        >
          Cancel
        </base-button>
        <base-button
          class="add-client-button"
          inverse
          :disabled="loading"
          @click="onSubmit"
        >
          <font-awesome-icon
            v-if="loading"
            icon="spinner"
            class="mr-1"
            spin
          />
          {{ loading ? "Adding Client" : "Add Client" }}
        </base-button>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { EventBus } from '@/event-bus';
import ProfileSelectorContainer from './NewCustomerProfileSelector';

export default {
  name: 'AddClient',
  components: {
    ProfileSelectorContainer,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    externalIdOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      assigned_team_owner: {},
      image: '',
      phone_numberRequired: true,
      emailRequired: true,
      client: {
        first_name: '',
        last_name: '',
        email: '',
        external_id: '',
      },
      mask: '##/##/####',
    };
  },
  computed: {
    ...mapState({
      customerFieldDisplayNames: (state) => state.customers.customerFieldDisplayNames || {},
    }),

    assigned() {
      if (Object.keys(this.assigned_team_owner).length !== 0) {
        return this.assigned_team_owner;
      }
      return this.$store.state.profiles.item;
    },
    filledInEverythingRequired() {
      // require assigning to a team member?
      //    waas originally active here; david comment out for Burberry
      /*
      if (this.isAdminMode && Object.keys(this.assigned || {}).length === 0) {
        return false;
      }
      */

      // TODO how to use `this.$refs.addClientForm.validate() to do
      //      all the blank-field checking via vee-validate?
      //
      // eslint-disable-next-line
      for (const field of this.getRequiredFields()) {
        if (!this.client[field]) {
          return false;
        }
      }
      return true;
    },
    isAdminMode() {
      return this.$store.state.account.isAdminMode;
    },
  },
  beforeMount() {
    this.$store.dispatch('customers/fetchCustomerRequiredFields');
  },
  methods: {
    isPhoneNumber() {
      if (typeof this.client.phone_number !== 'undefined') {
        if (this.client.phone_number.length > 0) {
          this.emailRequired = false;
        } else {
          this.emailRequired = true;
        }
      } else {
        this.emailRequired = true;
      }
    },
    isEmail() {
      if (this.client.email.length > 0) {
        this.phone_numberRequired = false;
      } else {
        this.phone_numberRequired = true;
      }
    },
    loadImage() {
      this.$refs.upload.click();
    },
    onSubmit() {
      this.$refs.addClientForm.validate().then((result) => {
        if (this.client.birth_date) {
          // eslint-disable-next-line
          if (!this.$moment(this.client.birth_date, 'MM/DD/YYYY', true)._isValid ) {
            this.$emit('closeModal');
            EventBus.$emit(
              'toast-error',
              'Birthday must be in this format: MM/DD/YYYY',
            );
            return;
          }
        }
        if (result) {
          if (this.isAdminMode && this.assigned) {
            this.$mixpanel.track('Stylist_Created', {
              user_email: this.client,
              account_id: this.$store.state.account.user.user.email,
            });
            this.client.assigned_team_owner = this.assigned.pk;
          }
          this.$emit('closeModal');
          const data = { ...this.client };
          if (!data.external_id) {
            delete data.external_id;
          }
          this.$emit('submit', data);
        }
      });
    },
    placeholderText(field) {
      const columnToDisplayName = {
        first_name: 'First Name',
        last_name: 'Last Name',
        email: 'Email',
        location: 'City',
        phone_number: 'Phone Number',
      };
      const text = columnToDisplayName[field] || field;
      // if (this.isRequiredField(field)) {
      //   text += ' (Required)';
      // }
      return text;
    },
    readFile(event) {
      this.uploadAndPatch(event.target.files[0]);
    },
    uploadAndPatch(file) {
      const reader = new FileReader();
      this.client.image = file;

      reader.readAsDataURL(file);
      reader.onload = () => {
        this.image = reader.result;
      };
      reader.onerror = (error) => {
        // console.log('Error: ', error);
      };
    },
    download() {
      this.$emit('download');
    },
    isRequiredField(field) {
      const required = this.getRequiredFields();
      return required.indexOf(field) !== -1;
    },
    getRequiredFields() {
      return this.$store.state.customers.customerRequiredFields || [];
    },
    onInputClicked(evt) {
      const vm = this;
      this.$nextTick().then(() => {
        vm.$refs[`${evt.target.name}_provider`].reset();
      });
    },
    permissionViewsAllClient() {
      return this.usePermission('view_all_clients') || this.useFeature('edits_sendable_to_all_institution_clients');
    },
  },
};
</script>

<style lang="scss" scoped>
.avatar-placeholder {
  width: 200px;
  height: 200px;
  cursor: pointer;

  &__add-icon {
    position: absolute;
    bottom: 12px;
    right: 15px;
    cursor: pointer;
  }
}
.avatar {
  width: 200px;
  height: 200px;
  border-radius: 150px;
  object-fit: cover;
}
.avatar-file {
  display: none;
}
input[type="text"],
input[type="email"] {
  height: 40px;
  width: 100%;
  padding: 12px 16px !important;
  color: $dark-black !important;
  border: 1px solid $gray-light-4;
}
.was-validated .form-control:invalid, .was-validated .form-control--clean:invalid, .form-control.is-invalid, .is-invalid.form-control--clean{
  border-color: $validation-error-color;
}
::placeholder {
  color: $dark-black !important;
  opacity: 1;
  font-size: 14px !important;
}
.divider {
  margin-top: 20px !important;
  margin-bottom: 40px !important;
  border-color: $gray-light-4;
}
.buttons-container {
  margin-top: 74px;

  &__small {
    margin-top: 46px;
  }
}
::v-deep .base-button--inverse {
  width: 168px;
  height: 40px;
  display: flex;
  justify-content: center;
  margin-inline: 0;
  background-color: $dark-black;
  font-family: $font-family-sans-serif-medium;
  font-weight: 600;
}
::v-deep .base-heading {
  font-family: $font-family-timepos-headline;
  font-weight: normal;
  font-style: normal;
  margin-bottom: 0;
  letter-spacing: normal;
}

// Grids
.add-client-grid{
  display: grid;
  grid-gap: 28px;
  grid-template-columns: [avatar] 193px [form] 1fr;
  align-items: center;

  &__form{
    display: grid;
    grid-row-gap: 12px;
  }
}
.form-grid{
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 12px;
  grid-row-gap: 12px;
}
.add-client-cancel-button {
  font-family: $font-family-sans-serif-medium;
  font-weight: 600;
  padding: 5px 15px;
}
.add-client-button {
  width: 160px;
}

@media (max-width: map-get($breakpoints,'lg')) {
  .avatar-placeholder {
    width: 200px;
    height: 200px;

    &__add-icon {
      bottom: 9px;
      right: 24px;
    }
  }
  .avatar {
    width: 200px;
    height: 200px;
  }
  ::v-deep .base-button--inverse {
    width: 160px;
  }
  .form-grid{
    grid-column-gap: 16px;
  }
  .buttons-container {
    margin-top: 48px;
  }
  .add-client-grid{
    grid-gap: 24px;
  }
}
</style>
